import { ExternalCheckoutAddressDialog } from "@/components/checkout/ExternalCheckoutAddressDialog";
import { useNeedsExternalCheckoutAddress } from "@/components/checkout/ExternalCheckoutAddressDialog/useNeedsExternalCheckoutAddress";
import { ExternalCheckoutDialog } from "@/components/checkout/ExternalCheckoutDialog";
import { LiveProvenanceBadge } from "@/components/provenance/LiveProvenanceBadge";
import { Badge } from "@/components/ui/badge";
import { useKeyboardActivation } from "@/hooks/useKeyboardActivation";
import { sellerCartItemCount } from "@/utils/cart";
import { formatPrice } from "@/utils/money";
import { pluralCount } from "@/utils/text";
import { Button } from "@medusajs/ui";
import type { SalonStockerHttpTypes } from "@repo/api-sdk";
import { cn } from "@repo/design-system/utils";
import { useNavigate } from "@tanstack/react-router";
import { CheckCircle2, ChevronDown } from "lucide-react";
import { memo, useCallback, useRef, useState } from "react";

type StoreSellerCart = SalonStockerHttpTypes.StoreSellerCart;

/** Formats a handoff timestamp as e.g. " on Jul 16" (empty on a bad date). */
function formatHandoffDate(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "";
  return ` on ${date.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric"
  })}`;
}

export interface AccordionHeaderProps {
  /**
   * The seller cart data
   */
  cart: StoreSellerCart;
  /**
   * Whether accordion is expanded
   */
  isExpanded: boolean;
  /**
   * Toggle callback
   */
  onToggle: () => void;
  /**
   * Display variant
   */
  variant: "by-supplier" | "all-items";
  /**
   * ID for accessibility
   */
  headerId: string;
  /**
   * ID of content region for aria-controls
   */
  contentId: string;
}

/**
 * Accordion header component for seller cart.
 * Shows seller info, item count, total, and checkout button.
 * Includes expand/collapse toggle for "all-items" variant.
 */
function AccordionHeaderComponent({
  cart,
  isExpanded,
  onToggle,
  variant,
  headerId,
  contentId
}: AccordionHeaderProps) {
  const navigate = useNavigate();
  const [externalDialogOpen, setExternalDialogOpen] = useState(false);
  const [addressDialogOpen, setAddressDialogOpen] = useState(false);
  // The click-time address check can wait on the network (cold cache). The ref
  // guards against a second click synchronously, before a re-render could
  // disable the button; the state shows the wait on the button.
  const checkingAddressRef = useRef(false);
  const [checkingAddress, setCheckingAddress] = useState(false);
  const needsAddress = useNeedsExternalCheckoutAddress();

  const itemCount = sellerCartItemCount(cart);

  const supportsExternalCheckout = cart.seller.supports_external_checkout;
  const handedOffAt = cart.handed_off_at;

  // Per-cart checkout. Sellers who sell on their own store (external checkout)
  // get an interstitial that redirects to that store; everyone else uses the
  // internal Stripe checkout flow.
  //
  // A buyer with no default shipping address is asked for one first: the
  // supplier's checkout is prefilled server-side from it, and the URL is only
  // requested once the interstitial opens - after the address is saved.
  const handleCheckout = useCallback(
    async (e: React.MouseEvent) => {
      e.stopPropagation(); // Prevent accordion toggle
      if (supportsExternalCheckout) {
        if (checkingAddressRef.current) return;
        checkingAddressRef.current = true;
        setCheckingAddress(true);
        try {
          if (await needsAddress()) {
            setAddressDialogOpen(true);
          } else {
            setExternalDialogOpen(true);
          }
        } finally {
          checkingAddressRef.current = false;
          setCheckingAddress(false);
        }
        return;
      }
      navigate({
        to: "/checkout",
        search: {
          cart_ids: cart.cart_id,
          step: "information"
        }
      });
    },
    [cart.cart_id, navigate, needsAddress, supportsExternalCheckout]
  );

  // Saved: swap the address dialog for the interstitial (one dialog at a time).
  // The redirect itself stays on the interstitial's own Continue click.
  const handleAddressSaved = useCallback(() => {
    setAddressDialogOpen(false);
    setExternalDialogOpen(true);
  }, []);

  // Row-level keyboard support (Enter/Space) to mirror native <button>
  // behaviour, since the row can't literally be a <button> - it contains
  // the real Checkout <Button> as a nested interactive element.
  const handleRowKeyDown = useKeyboardActivation(onToggle);

  return (
    // No card chrome of its own - the parent `SellerCartAccordion` owns the
    // border and radius, so this row is the top of that box rather than a
    // sealed card sitting above the expanded body.
    <>
      <div
        role="button"
        tabIndex={0}
        onClick={onToggle}
        onKeyDown={handleRowKeyDown}
        aria-expanded={isExpanded}
        aria-controls={contentId}
        aria-label={
          isExpanded
            ? `Collapse ${cart.seller.name} cart`
            : `Expand ${cart.seller.name} cart`
        }
        className="flex cursor-pointer items-center gap-3 p-3 transition-colors hover:bg-gray-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-purple-500 sm:p-4"
      >
        {/* Chevron - decorative now; the whole row toggles the accordion */}
        <span
          className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-gray-100 text-gray-500 transition-colors"
          aria-hidden="true"
        >
          <ChevronDown
            className={cn(
              "h-5 w-5 transition-transform duration-200",
              !isExpanded && "-rotate-90"
            )}
          />
        </span>

        {/* On a phone the name and item count take the full width on top - so a
            long supplier name never has to wrap around the action beside it - and
            the total + checkout share the line below. From `sm` the name/count
            take the left and the total + action sit inline on the right. */}
        <div className="flex min-w-0 flex-1 flex-col gap-1 sm:flex-row sm:items-center sm:gap-4">
          {/* Seller name and item count */}
          <div className="min-w-0 sm:flex-1">
            <h3 id={headerId} className="text-base font-semibold text-gray-900">
              {cart.seller.name}
            </h3>
            <p className="text-sm text-gray-500">
              {pluralCount(itemCount, "item")}
            </p>
          </div>

          {/* Total + checkout action (or the handed-off status once sent to the
              supplier), sharing one line: the total on the left, the action on
              the right. On a phone this is the line beneath the name/count; from
              `sm` it sits at the end of the row. */}
          <div className="flex items-center justify-between gap-3 sm:justify-end">
            {/* The marker follows the figure. This header shows the same total
                as the card body, and the card collapses - so without it here,
                collapsing a card is enough to make the provenance disappear
                while the number it describes stays on screen. */}
            <div className="flex min-w-0 items-center gap-2">
              <p className="text-lg font-bold text-gray-900">
                {formatPrice(cart.totals.total)}
              </p>
              <LiveProvenanceBadge
                provenance={cart.totals.provenance}
                subject="total"
                source={cart.seller.name}
              />
            </div>

            {handedOffAt ? (
              <Badge
                variant="secondary"
                className="whitespace-nowrap text-emerald-700"
              >
                <CheckCircle2 className="mr-1 h-3.5 w-3.5" aria-hidden="true" />
                Sent{formatHandoffDate(handedOffAt)}
              </Badge>
            ) : (
              <Button
                onClick={handleCheckout}
                isLoading={checkingAddress}
                size="small"
                className="whitespace-nowrap"
                aria-label={`Checkout ${cart.seller.name} cart`}
              >
                Checkout →
              </Button>
            )}
          </div>
        </div>
      </div>

      {supportsExternalCheckout && (
        <>
          <ExternalCheckoutAddressDialog
            open={addressDialogOpen}
            onOpenChange={setAddressDialogOpen}
            onSaved={handleAddressSaved}
          />
          <ExternalCheckoutDialog
            cart={cart}
            open={externalDialogOpen}
            onOpenChange={setExternalDialogOpen}
          />
        </>
      )}
    </>
  );
}

export const AccordionHeader = memo(AccordionHeaderComponent);
